/**
 * Avatar-dropdown "Accounts / Switch account" section (Gordon 2026-07-20) — the
 * GitHub/Google-style remembered-accounts switcher.
 *
 * Lists the accounts this browser has signed into (the current one flagged with a
 * line-art check), lets you switch to another without re-entering a password when
 * its stored token is still valid, "Add account" (a fresh login that keeps the
 * current session alive), forget one account, or forget all saved accounts.
 *
 * Reuses the dropdown's existing Divider / DropdownItem chrome (./styles) so it is
 * byte-consistent with the neighbouring menu rows, and mirrors the a11y pattern of
 * the sibling AvatarLinkedWheelsSection (role/tabIndex/aria-label + Enter/Space).
 *
 * Placed at the TOP of the logged-in dropdown (a different hunk from PR #2090's
 * linked-wheels section at the bottom) so the two compose without colliding.
 *
 * Flag-gated: ProfileMenu only mounts this when REACT_APP_ACCOUNT_SWITCHER is on,
 * and it also self-gates here (defense in depth) — so when OFF nothing renders and
 * the registry is never written (byte-identical to today).
 *
 * Switch = provably no cross-account bleed: switchToAccount() flushes react-query +
 * redux-persist + account-scoped storage, then HARD reloads into the new account.
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import CircularProgress from "@mui/material/CircularProgress";

import * as Styled from "./styles";
import { colors } from "../../utils";
import { persistor } from "../../redux";
import { ACCOUNT_SWITCHER_ENABLED } from "./accountSwitcherFlag";
import UserImage from "../UserImage";
import { AVATAR_WHEEL_FRAMES_ENABLED } from "../../constants/featureFlags";
import {
  getAccounts,
  upsertAccount,
  removeAccount,
} from "../../services/accountRegistry";
import {
  validateAccountToken,
  switchToAccount,
  currentAccountEntry,
} from "../../services/accountSwitch";
import { buildReauthLoginRedirect } from "../../utils/loginRedirect";

const LABEL_GREY = colors.StrokePrimary || colors.textGrey || "#667085";

function AccountAvatar({ src, label }) {
  // Wheel-Frame convergence (Gordon law all-avatars-wheel-frames): the account
  // avatar renders inside the canonical <UserImage> frame; a no-photo account
  // shows the framed placeholder (fallback INSIDE the frame) instead of the bare
  // outline icon.
  // QA-151 (Kelly, 2026-08-19): bumped 22->32px to match the Facebook-style
  // account-switcher reference (video frame-05/06, C:\iYuga\qa-notes\media\
  // QA-151) — FB's profile-switcher rows use a visibly larger circular photo
  // than our old compact icon-row size.
  if (AVATAR_WHEEL_FRAMES_ENABLED) {
    return (
      <UserImage
        image={src}
        size={32}
        ariaHidden
        title={label}
        style={{ marginRight: 12, flex: "0 0 auto" }}
      />
    );
  }
  if (src) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        width={32}
        height={32}
        style={{
          borderRadius: "50%",
          objectFit: "cover",
          marginRight: 12,
          flex: "0 0 auto",
        }}
      />
    );
  }
  return (
    <AccountCircleOutlinedIcon
      aria-hidden="true"
      style={{ fontSize: 32, marginRight: 12, color: LABEL_GREY, flexShrink: 0 }}
      titleAccess={label}
    />
  );
}
AccountAvatar.propTypes = {
  src: PropTypes.string,
  label: PropTypes.string,
};

function AccountRow({ account, isActive, isBusy, onSwitch, onForget }) {
  const label = account.name || account.email || `User ${account.id}`;
  const activate = () => {
    if (isActive || isBusy) return;
    onSwitch(account);
  };
  return (
    <Styled.DropdownItem
      role="button"
      tabIndex={0}
      aria-label={isActive ? `${label} (current account)` : `Switch to ${label}`}
      aria-current={isActive ? "true" : undefined}
      title={account.email || label}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      }}
      // QA-151: the FB-style reference (frame-05/06) gives the current row a
      // visibly tinted rounded row, not just bold text — reuse the existing
      // DropdownItem `isActive` background prop (previously only wired for
      // the Analytics row) so the current account reads at a glance.
      isActive={isActive}
      style={{ height: "auto", paddingTop: 8, paddingBottom: 8 }}
    >
      <AccountAvatar src={account.avatar} label={label} />
      <span
        style={{
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          flex: "1 1 auto",
          lineHeight: 1.25,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 150,
            fontWeight: isActive ? 700 : 500,
          }}
        >
          {label}
        </span>
        {account.email && account.email !== label && (
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 150,
              fontSize: 11,
              color: LABEL_GREY,
            }}
          >
            {account.email}
          </span>
        )}
      </span>
      {isBusy ? (
        <CircularProgress
          size={16}
          aria-label="Switching account"
          style={{ marginLeft: 8, color: colors.PrimaryPurple, flexShrink: 0 }}
        />
      ) : isActive ? (
        <CheckOutlinedIcon
          aria-hidden="true"
          style={{
            fontSize: 18,
            marginLeft: 8,
            color: colors.PrimaryPurple,
            flexShrink: 0,
          }}
        />
      ) : (
        <span
          role="button"
          tabIndex={0}
          aria-label={`Forget ${label}`}
          title={`Forget ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            onForget(account);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              onForget(account);
            }
          }}
          style={{
            marginLeft: 8,
            display: "inline-flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <CloseOutlinedIcon style={{ fontSize: 16, color: LABEL_GREY }} />
        </span>
      )}
    </Styled.DropdownItem>
  );
}
AccountRow.propTypes = {
  account: PropTypes.object.isRequired,
  isActive: PropTypes.bool,
  isBusy: PropTypes.bool,
  onSwitch: PropTypes.func.isRequired,
  onForget: PropTypes.func.isRequired,
};

/**
 * @param {object} props
 * @param {number|string} props.currentUserId  signed-in user's id (from keyCloak)
 * @param {object} props.keyCloak               live { token, user } (to remember current)
 * @param {object} props.userProfile            redux userProfile (name/email/avatar)
 * @param {() => void} props.onRequestClose      close the dropdown before SPA nav
 */
export default function AccountSwitcherSection({
  currentUserId,
  keyCloak,
  userProfile,
  onRequestClose,
}) {
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const [accounts, setAccounts] = useState([]);
  const [switchingId, setSwitchingId] = useState(null);

  // Remember the CURRENTLY signed-in account (idempotent upsert), then load the
  // list. All registry writes live behind the flag → OFF means zero writes.
  useEffect(() => {
    if (!ACCOUNT_SWITCHER_ENABLED) return;
    const entry = currentAccountEntry(keyCloak, userProfile);
    if (entry) {
      upsertAccount(entry);
    }
    setAccounts(getAccounts());
  }, [
    keyCloak && keyCloak.token,
    currentUserId,
    userProfile && userProfile.profile_image,
    userProfile && userProfile.name,
  ]);

  const currentId = currentUserId != null ? String(currentUserId) : null;

  if (!ACCOUNT_SWITCHER_ENABLED) return null;
  if (currentId == null) return null;

  const handleSwitch = async (account) => {
    if (switchingId) return;
    setSwitchingId(account.id);
    const valid = await validateAccountToken({
      token: account.token,
      userId: account.id,
    });
    if (valid) {
      // hard reload into the new account — clears all caches by construction
      switchToAccount({
        account: { id: account.id, token: account.token, user: { id: account.id } },
        queryClient,
        persistor,
      });
      return; // navigating away
    }
    // BUG #700 (Gordon 2026-08-22): token no longer valid → cannot switch
    // silently. Instead of a vague `toast.info(...)` and a bare prefilled login,
    // route to the re-auth login URL (`reason=session-expired` + prefilled
    // email). The Login page reads `reason=session-expired` and renders an
    // explanatory banner saying WHICH session expired and WHY they are here —
    // no toast needed.
    setSwitchingId(null);
    if (onRequestClose) onRequestClose();
    nav(buildReauthLoginRedirect(account.email));
  };

  const handleForget = (account) => {
    setAccounts(removeAccount(account.id));
  };

  const handleAddAccount = () => {
    // keep the current session alive; a fresh login will remember the new account.
    if (onRequestClose) onRequestClose();
    nav("/login?addAccount=1");
  };

  // Gordon 2026-07-24 (observed live): "Switch accounts should be UNDER the username
  // not above it." The ACCOUNTS/switch affordance previously rendered ABOVE every row,
  // so the first thing in the menu was the switcher rather than WHO YOU ARE. Lead with
  // the signed-in identity, then offer switching beneath it (the GitHub pattern).
  const activeAccount = accounts.find((a) => a.id === currentId);
  const otherAccounts = accounts.filter((a) => a.id !== currentId);

  const renderRow = (account) => (
    <AccountRow
      key={account.id}
      account={account}
      isActive={account.id === currentId}
      isBusy={switchingId === account.id}
      onSwitch={handleSwitch}
      onForget={handleForget}
    />
  );

  return (
    <>
      {/* 1. WHO YOU ARE — always first. */}
      {activeAccount && renderRow(activeAccount)}

      {/* 2. Switching lives UNDERNEATH the identity it switches away from.
          QA-151 (Kelly, 2026-08-19): the FB-style reference (video
          frame-05/06, C:\iYuga\qa-notes\media\QA-151) shows plain stacked
          account rows with NO uppercase "ACCOUNTS" section label between
          them — dropped the label div to match; the group's a11y name now
          lives on the wrapping <section> below instead of a visible label. */}
      <section aria-label="Switch account">
        {otherAccounts.map(renderRow)}
      </section>

      <Styled.DropdownItem
        role="button"
        tabIndex={0}
        aria-label="Add another account"
        onClick={handleAddAccount}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleAddAccount();
          }
        }}
      >
        <PersonAddOutlinedIcon
          aria-hidden="true"
          style={{ fontSize: 18, marginRight: 10, color: LABEL_GREY, flexShrink: 0 }}
        />
        <span>Add account</span>
      </Styled.DropdownItem>

      {/* The bulk "Forget saved accounts" row was REMOVED (Gordon 2026-08-18):
          it was a redundant + destructive bulk action. Per-account removal is
          the ✕ on each account row above (AccountRow's "Forget <name>"), which
          is kept as the single, granular removal affordance. */}

      <Styled.Divider />
    </>
  );
}

AccountSwitcherSection.propTypes = {
  currentUserId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  keyCloak: PropTypes.object,
  userProfile: PropTypes.object,
  onRequestClose: PropTypes.func,
};
